import { useAppRouter } from '@/common/route/useAppRouter';
import { decrypt, encrypt } from '@/common/utils/cryptoUtils';
import { useCrudActions } from '@/common/view/useCrudActions';
import { useAppStore } from '@/modules/apps/store';
import { useAuthStore } from '@/modules/auth/store';
import { ROUTE_NAME_LOGIN } from '@/modules/login/router';
import { ROUTE_NAME_HOMEPAGE } from '@/modules/main/router';
import { useUiStore } from '@/modules/ui/store';
import authService from '@/services/auth';
import { useVuelidate } from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';
import { reactive, ref } from 'vue';
import { useToast } from 'vue-toastification';

export function useLogin() {
    const router = useAppRouter();
    const toast = useToast();
    const authStore = useAuthStore();
    const appStore = useAppStore();
    const uiStore = useUiStore();

    const loading = ref(false);

    const model = reactive({
        email: '',
        password: ''
    });

    const validations = {
        email: { required, email },
        password: { required }
    };

    const v$ = useVuelidate(validations, model);

    const login = useCrudActions().executeAction({
        canExecute: () => !loading.value,
        validator: v$,
        request: async () => {
            loading.value = true;
            const trustToken = await decrypt(authStore.trustToken);
            return authService.loginByEmail(model.email, model.password, trustToken);
        },
        onSuccess: async ({ data }) => {
            const token = data.token;
            const isTrusted = data.isTrusted;
            const encryptedToken = await encrypt(token);
            authStore.setToken(encryptedToken);

            if (isTrusted) {
                await authStore.loadRoles();
                await appStore.loadApps();
                uiStore.resetLoading();
                toast.success('Login succeeded');
                router.push({ name: ROUTE_NAME_HOMEPAGE });
            } else {
                router.push({ name: ROUTE_NAME_LOGIN.VERIFY });
            }
        },
        onFinally: () => (loading.value = false) // always reset
    });

    return { model, v$, login, loading };
}
